import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface Permission {
  id: number;
  key: string;
  name: string;
  category: string;
  description: string | null;
  /** Whether the current user holds this permission and may grant/revoke it. */
  assignable?: boolean;
}

export interface Role {
  id: number;
  name: string;
  description: string | null;
  is_custom: boolean;
  school_id: number | null;
  Permissions?: Permission[];
  /** Whether the current user holds every permission on this role. */
  can_assign?: boolean;
}

export interface SchoolUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  user_type: string;
  is_active: boolean;
  preferredName: string | null;
  Roles: Role[];
  Permissions?: Permission[];
  /** Saved avatar image, when the response includes a Profile (absent if none saved). */
  Profile?: { profile_picture_path: string | null };
}

@Injectable({ providedIn: 'root' })
export class RoleService {
  private base = '/api/roles';
  private http = inject(HttpClient);

  /**
   * Fetches all available roles with their permissions.
   */
  getRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(this.base);
  }

  /**
   * Creates a new custom role with the given name, optional description, and
   * selected permissions.
   */
  createRole(name: string, description: string | null, permissionIds: number[]): Observable<Role> {
    return this.http.post<Role>(this.base, { name, description, permissionIds });
  }

  /**
   * Updates an existing custom role's name, description, and permissions.
   */
  updateRole(
    id: number,
    name: string,
    description: string | null,
    permissionIds: number[],
  ): Observable<Role> {
    return this.http.put<Role>(`${this.base}/${id}`, { name, description, permissionIds });
  }

  /**
   * Deletes a custom role.
   */
  deleteRole(id: number): Observable<{ message: string; id: number }> {
    return this.http.delete<{ message: string; id: number }>(`${this.base}/${id}`);
  }

  /**
   * Fetches all users in the current admin's school with their assigned roles.
   */
  getSchoolUsers(): Observable<SchoolUser[]> {
    return this.http.get<SchoolUser[]>(`${this.base}/school-users`);
  }

  /**
   * Assigns a role to a user.
   */
  assignRole(userId: number, roleId: number): Observable<SchoolUser> {
    return this.http.post<SchoolUser>(`${this.base}/assign`, { userId, roleId });
  }

  /**
   * Removes a role from a user.
   */
  removeRole(userId: number, roleId: number): Observable<SchoolUser> {
    return this.http.post<SchoolUser>(`${this.base}/remove`, { userId, roleId });
  }

  /**
   * Fetches all available permissions.
   */
  getPermissions(): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${this.base}/permissions`);
  }

  /**
   * Fetches a single user with their roles and direct permissions.
   */
  getUserWithDetails(userId: number): Observable<SchoolUser> {
    return this.http.get<SchoolUser>(`${this.base}/users/${userId}`);
  }

  /**
   * Bulk updates a user's roles and direct permissions.
   */
  updateUserRolesAndPermissions(
    userId: number,
    roleIds: number[],
    permissionIds: number[],
  ): Observable<SchoolUser> {
    return this.http.put<SchoolUser>(`${this.base}/users/${userId}`, {
      roleIds,
      permissionIds,
    });
  }
}
